import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { getExam, checkExamAvailability, checkExamSubmission } from '../../../api'; // ✅ Added checkExamSubmission
import { 
  ClockIcon, 
  CalendarIcon, 
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  LockClosedIcon,
  DocumentTextIcon,
  CheckBadgeIcon // ✅ New icon for completed exams
} from '@heroicons/react/24/outline';

const GetExams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const timeIntervalRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user) {
      Swal.fire('Unauthorized', 'Please log in to access exams.', 'warning');
      navigate('/login');
      return;
    }

    // Load exams immediately
    loadExams();

    // Auto-refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      console.log('🔄 Auto-refreshing exam availability...');
      loadExams(true); // Pass true for background refresh
    }, 30000);

    // Update current time every second for countdown
    timeIntervalRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [navigate]);

  const loadExams = async (isBackgroundRefresh = false) => {
    if (isBackgroundRefresh) {
      setRefreshing(true);
    }

    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const data = await getExam();
      
      // ✅ Check both availability AND submission status for each exam
      const examsWithAvailability = await Promise.all(
        data.map(async (exam) => {
          try {
            // Check availability
            const availabilityResult = await checkExamAvailability(exam.eid);
            
            // ✅ Check if already submitted
            const submissionResult = await checkExamSubmission(exam.eid, user.uid);
            
            return {
              ...exam,
              isAvailable: availabilityResult.available || false,
              availabilityMessage: availabilityResult.message || '',
              availabilityReason: availabilityResult.reason || '',
              timeSlot: availabilityResult.time_slot || null,
              currentServerTime: availabilityResult.current_time || null,
              startTime: availabilityResult.start_time || exam.start_time,
              endTime: availabilityResult.end_time || exam.end_time,
              // ✅ NEW: Submission status
              isSubmitted: submissionResult.submitted || false,
              submissionMessage: submissionResult.message || ''
            };
          } catch (error) {
            console.error(`❌ Error checking status for exam ${exam.eid}:`, error);
            return {
              ...exam,
              isAvailable: false,
              availabilityMessage: 'Error checking availability',
              isSubmitted: false
            };
          }
        })
      );
      
      setExams(examsWithAvailability);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('❌ Error loading exams:', error);
      if (loading) {
        Swal.fire('Error', 'Failed to fetch exams.', 'error');
      }
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Calculate countdown to exam start
  const getCountdown = (exam) => {
    if (!exam.date || !exam.start_time || exam.schedule_enabled != 1) return null;
    
    try {
      const examDate = new Date(exam.date);
      const [hours, minutes] = exam.start_time.split(':');
      examDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      const diff = examDate - currentTime;
      
      if (diff <= 0) return null;
      
      const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
      const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secondsLeft = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (hoursLeft > 0) {
        return `${hoursLeft}h ${minutesLeft}m`;
      } else if (minutesLeft > 0) {
        return `${minutesLeft}m ${secondsLeft}s`;
      } else {
        return `${secondsLeft}s`;
      }
    } catch (error) {
      return null;
    }
  };

  const handleTakeExam = async (exam) => {
    try {
      // ✅ Check if already submitted first
      if (exam.isSubmitted) {
        Swal.fire({
          icon: 'info',
          title: 'Already Submitted',
          text: 'You have already submitted this exam. You cannot retake it.',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      const availabilityResult = await checkExamAvailability(exam.eid);
      
      if (!availabilityResult.available) {
        Swal.fire({
          icon: 'warning',
          title: 'Exam Not Available',
          html: `<p>${availabilityResult.message}</p>`,
          confirmButtonColor: '#3085d6'
        });
        loadExams();
        return;
      }

      // Navigate to instructions page
      navigate(`/student/exam-instruction/${exam.eid}`);
    } catch (error) {
      Swal.fire('Error', 'Failed to verify exam availability', 'error');
    }
  };

  const handleManualRefresh = () => {
    loadExams(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <p className="mt-4 text-gray-600 font-medium">Loading exams...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                  <AcademicCapIcon className="w-6 h-6 text-white" />
                </div>
                <span>Available Exams</span>
              </h1>
              <p className="text-sm text-gray-500 mt-2 ml-13">Select an exam to begin your assessment</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Auto-refresh indicator */}
              <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <ArrowPathIcon className={`w-4 h-4 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-xs text-blue-700 font-medium">Auto-refresh: 30s</span>
              </div>
              
              {/* Manual refresh button */}
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm">Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Exams Grid */}
        {exams.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DocumentTextIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Exams Available</h3>
            <p className="text-gray-600">There are no exams scheduled at the moment. Please check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exams.map((exam) => {
              const countdown = getCountdown(exam);
              
              return (
                <div
                  key={exam.eid}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
                    exam.isSubmitted
                      ? 'border-purple-500 hover:border-purple-600'
                      : exam.isAvailable 
                      ? 'border-green-500 hover:border-green-600' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {/* Card Header with Status */}
                  <div className={`p-4 ${
                    exam.isSubmitted
                      ? 'bg-gradient-to-r from-purple-500 to-violet-500'
                      : exam.isAvailable 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                      : 'bg-gradient-to-r from-gray-400 to-gray-500'
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-white truncate">{exam.name}</h3>
                      {exam.isSubmitted ? (
                        <CheckBadgeIcon className="w-6 h-6 text-white" />
                      ) : exam.isAvailable ? (
                        <CheckCircleIcon className="w-6 h-6 text-white animate-pulse" />
                      ) : (
                        <XCircleIcon className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <p className="text-white text-xs mt-1 font-medium">
                      {exam.isSubmitted 
                        ? '● Submitted' 
                        : exam.isAvailable 
                        ? '● Available Now' 
                        : '● Not Available'}
                    </p>
                  </div>

                  {/* Card Body */}
                  <div className="p-5">
                    {/* Exam Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <ClockIcon className="w-5 h-5 text-orange-600" />
                        <span className="font-medium">Duration:</span>
                        <span className="text-gray-600">{exam.duration} mins</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                        <span className="font-medium">Total Marks:</span>
                        <span className="text-gray-600">{exam.tmarks}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <CalendarIcon className="w-5 h-5 text-red-600" />
                        <span className="font-medium">Date:</span>
                        <span className="text-gray-600">{exam.date}</span>
                      </div>

                      {/* Time Slot */}
                      {exam.schedule_enabled == 1 && exam.start_time && exam.end_time && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                          <ClockIcon className="w-5 h-5 text-indigo-600" />
                          <span className="font-medium">Time:</span>
                          <span className="text-gray-600">
                            {exam.start_time.substring(0, 5)} - {exam.end_time.substring(0, 5)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ✅ Submitted Badge */}
                    {exam.isSubmitted && (
                      <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-violet-50 border-l-4 border-purple-500 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <CheckBadgeIcon className="w-5 h-5 text-purple-600" />
                          <div>
                            <p className="text-xs text-purple-700 font-semibold">Completed</p>
                            <p className="text-xs text-purple-600">You have already submitted this exam</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Countdown Timer */}
                    {countdown && !exam.isSubmitted && (
                      <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="w-5 h-5 text-blue-600 animate-pulse" />
                          <div>
                            <p className="text-xs text-blue-700 font-semibold">Starts In</p>
                            <p className="text-lg font-bold text-blue-800">{countdown}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Availability Message */}
                    {!exam.isAvailable && !countdown && !exam.isSubmitted && (
                      <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-red-700 font-medium">
                            {exam.availabilityMessage}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <button
                      onClick={() => handleTakeExam(exam)}
                      disabled={!exam.isAvailable || exam.isSubmitted}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 ${
                        exam.isSubmitted
                          ? 'bg-purple-100 text-purple-700 cursor-not-allowed'
                          : exam.isAvailable
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {exam.isSubmitted ? (
                        <>
                          <CheckBadgeIcon className="w-5 h-5" />
                          <span>Submitted</span>
                        </>
                      ) : exam.isAvailable ? (
                        <>
                          <RocketLaunchIcon className="w-5 h-5" />
                          <span>Start Exam</span>
                        </>
                      ) : (
                        <>
                          <LockClosedIcon className="w-5 h-5" />
                          <span>Not Available</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GetExams;
